/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

/**
 * Helper function to decode HTML entities
 */
export const decodeHtmlEntities = (text: string): string => {
  const textArea = document.createElement('textarea');
  textArea.innerHTML = text;
  return textArea.value;
};

/**
 * Parse device attributes JSON.
 * Supports both legacy string payloads and object payloads.
 */
export const parseDeviceAttributes = (attributes: unknown): any => {
  if (!attributes) return {};

  // Newer payloads may already provide attributes as an object.
  if (typeof attributes === "object") {
    return attributes;
  }

  if (typeof attributes !== "string") {
    return {};
  }

  try {
    const decodedAttributes = decodeHtmlEntities(attributes);
    return JSON.parse(decodedAttributes);
  } catch (e) {
    return {};
  }
};

/**
 * Helper function to check if a bit is set in a bitmask
 */
export const isBitSet = (value: any, bit: number): boolean => {
  if (value === undefined || value === null || !Number.isInteger(bit) || bit < 0) {
    return false;
  }

  let numValue: number;

  if (typeof value === 'number') {
    numValue = value;
  } else if (typeof value === 'string') {
    const normalized = value.trim().replace(/,/g, '');
    if (!normalized) return false;

    if (/^0x[0-9a-f]+$/i.test(normalized)) {
      numValue = parseInt(normalized.slice(2), 16);
    } else if (/^0b[01]+$/i.test(normalized)) {
      numValue = parseInt(normalized.slice(2), 2);
    } else if (/^0o[0-7]+$/i.test(normalized)) {
      numValue = parseInt(normalized.slice(2), 8);
    } else {
      numValue = Number(normalized);
    }
  } else {
    numValue = Number(value);
  }

  if (!Number.isFinite(numValue)) return false;

  // Avoid 32-bit bitwise coercion so larger decimal masks still decode correctly.
  const normalizedValue = numValue < 0 ? (numValue >>> 0) : Math.trunc(numValue);
  const bitValue = Math.floor(normalizedValue / Math.pow(2, bit));
  return bitValue % 2 === 1;
};

/**
 * Helper function to check if power signal is valid
 */
export const isValidPowerSignal = (power: any): boolean => {
  if (!power && power !== 0) return false;
  const powerValue = parseFloat(power);
  if (isNaN(powerValue)) return false;
  return powerValue > -1000;
};

/**
 * Helper function to format power values
 */
export const formatPower = (power: any): string => {
  if (!power && power !== 0) return "N/A";
  const powerValue = parseFloat(power);
  if (isNaN(powerValue)) return "N/A";
  return powerValue <= -1000 ? "No Signal" : `${powerValue.toFixed(2)} dBm`;
};

/**
 * Helper function to check if optical signal is valid
 */
export const isValidOpticalSignal = (loss: any): boolean => {
  if (!loss && loss !== 0) return false;
  const lossValue = parseFloat(loss);
  if (isNaN(lossValue)) return false;
  return lossValue < 1000;
};

/**
 * Helper function to format optical loss
 */
export const formatOpticalLoss = (loss: any): string => {
  if (!loss && loss !== 0) return "N/A";
  const lossValue = parseFloat(loss);
  if (isNaN(lossValue)) return "N/A";
  return lossValue >= 1000 ? "No Signal" : `${lossValue.toFixed(2)} dBo`;
};

/**
 * Helper function to format On/Off values
 */
export const formatOnOff = (value: any): string => {
  if (value === undefined || value === null) return "N/A";
  return value === 1 || value === "1" ? "On" : "Off";
};

/**
 * Helper function to format gain mode
 */
export const formatGainMode = (mode: any): string => {
  if (mode === undefined || mode === null) return "N/A";
  return mode === 0 || mode === "0" ? "Manual" : "Autosymetrical";
};

/**
 * Get LED color based on device connection status and alarm state
 */
export const getAlarmLedColor = (isDisconnected: boolean, isAlarmActive: boolean): string => {
  if (isDisconnected) return "#9e9e9e"; // Gray
  return isAlarmActive ? "#f44336" : "#4caf50"; // Red : Green
};

/**
 * Get LED color for power/signal indicators
 */
export const getSignalLedColor = (isValid: boolean): string => {
  return isValid ? "#4caf50" : "#f44336"; // Green : Red
};
